import {
  el,
  list,
  setChildren,
  setAttr,
  mount,
  unmount
} from 'redom';

import { PencilBrush } from 'fabric';

import {
  displayElementAroundAnchor
} from 'utilities/element-position';

import { stringToSVG } from 'utilities';

import {
  hexToRgb,
  rgbToHex,
  getColorString
} from './utilities';

import './index.scss';

import pencilBrushIconString from 'assets/images/pencil-brush-icon.svg?raw';
import brushControlsIconString from 'assets/images/brush-icon.svg?raw';

import eraserIconString from 'assets/images/eraser-icon.svg?raw';

const eraserIcon = stringToSVG(eraserIconString);
const brushControlsIcon = stringToSVG(brushControlsIconString);

const pencilBrushIcon = stringToSVG(pencilBrushIconString);

export const brushTypes = {
  PENCIL: 'pencil',
  CIRCLE: 'circle',
  SPRAY: 'spray'
};

class SizeInput {

  constructor(configuration) {

    this.onChange = configuration.onChange;

    this.el = el('input.size-input', {
      type: 'range',
      min: configuration.min || 1,
      max: configuration.max || 100,
      value: configuration.size + '',
      oninput: this.handleInput.bind(this),
      step: configuration.step || .5,
      onclick: (event) => event.stopPropagation()
    });

  }

  handleInput(event) {

    event.stopPropagation();

    this.onChange(event.target.value);

  }

}

class WidthAndOpacity {

  constructor(configuration) {

    this.width = configuration.width;
    this.opacity = configuration.opacity;
    this.onChange = configuration.onChange;

    this.widthInput = new SizeInput({
      size: this.width,
      min: 1,
      max: 30,
      onChange: this.handleWidthChange.bind(this)
    });

    this.opacityInput = new SizeInput({
      size: this.opacity + '',
      min: '0.10',
      max: '1.00',
      step: '0.01',
      onChange: this.handleOpacityChange.bind(this)
    });

    this.el = el('.width-and-opacity', [
      el('.brush-input-row', [
        el('label', 'Width'),
        this.widthInput
      ]),
      el('.brush-input-row', [
        el('label', 'Opacity'),
        this.opacityInput
      ])
    ]);

  }

  handleWidthChange(width) {

    this.width = +width;

    this.onChange({
      width: this.width,
      opacity: this.opacity
    });

  }

  handleOpacityChange(opacity) {

    this.opacity = +opacity;

    this.onChange({
      width: this.width,
      opacity: 1 - this.opacity
    });

  }

}

class BrushControl {

  constructor(configuration) {

    this.isActive = configuration.isActive;
    this.tool = configuration.tool;
    this.controls = configuration.controls;

    this.sizeInput = new WidthAndOpacity({
      width: this.tool.width,
      opacity: this.tool.opacity,
      onChange: this.handleChange.bind(this)
    });

    this.el = el('button.brush-control', {
      onclick: this.handleClick.bind(this)
    }, [
      pencilBrushIcon.cloneNode(true)
    ]);

    if (this.isActive) {
      this.el.classList.add('active');
      mount(this.el, this.sizeInput);
      this.positionSizeInput();
    }

    this.setWidthUI();

  }

  setWidthUI() {

    setAttr(this.el.querySelector('circle'), {
      r: this.tool.width / 2,
      opacity: this.tool.opacity,
      fill: getColorString(this.tool.color, this.tool.opacity)
    });

  }

  handleChange(settings) {

    this.tool.setWidth(settings.width);
    this.tool.setOpacity(settings.opacity);
    this.setWidthUI();

  }

  positionSizeInput() {

    const element = this.sizeInput.el;
    const anchorElement = this.el;
    const viewportElement = void 0;
    const verticalOffset = 0;
    const horizontalOffset = 0;
    const startSide = 'top';
    const placeOnCenter = true;
    const positionableSides = ['top', 'bottom'];

    displayElementAroundAnchor(element, anchorElement, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides);

    setAttr(element, {
      style: {
        height: 'auto'
      }
    });

  }

  activate() {

    this.isActive = true;
    this.el.classList.add('active');
    mount(this.el, this.sizeInput);
    this.positionSizeInput();

  }

  deactivate() {

    this.isActive = false;
    this.el.classList.remove('active');
    unmount(this.el, this.sizeInput);

  }

  handleClick(event) {

    setAttr(this.el, {
      disabled: true
    });

    if (this.isActive === true) {
      this.isActive = false;
      this.el.classList.remove('active');
      unmount(this.el, this.sizeInput);
    } else {
      this.activate();
    }

    this.controls.toggleBrush();

    setAttr(this.el, {
      disabled: false
    });

  }

}
class EraseControl {

  constructor(configuration) {

    this.isActive = configuration.isActive;
    this.controls = configuration.controls;

    this.el = el('button.brush-control', {
      onclick: this.handleClick.bind(this)
    }, [
      eraserIcon.cloneNode(true)
    ]);

    if (this.isActive) {
      this.el.classList.add('active');
    }

  }

  handleClick(event) {

    setAttr(this.el, {
      disabled: true
    });

    if (this.isActive === true) {
      this.isActive = false;
      this.el.classList.remove('active');
      this.controls.deactivateEraserTool();
    } else {
      this.isActive = true;
      this.el.classList.add('active');
      this.controls.activateEraserTool();
    }

    setAttr(this.el, {
      disabled: false
    });

  }

}

class ColorControl {

  constructor(configuration) {

    this.isActive = configuration.isActive;
    this.tool = configuration.tool;
    this.controls = configuration.controls;

    this.el = el('input.color-input', {
      type: 'color',
      value: rgbToHex(this.tool.color),
      oninput: this.handleInput.bind(this),
      onclick: (event) => {
        event.stopPropagation();
        this.controls.activateBrush();
      }
    });

  }

  handleInput(event) {

    this.tool.setColor(hexToRgb(event.target.value));
    this.controls.brushControl.setWidthUI();

  }

}

class BrushControls {

  constructor(configuration) {

    this.canvas = configuration.canvas;
    this.isActive = this.determineActive();
    this.mouseLeaveTimer = null;
    this.brushTool = this.initializeBrushTool();
    this.eraserTool = new EraserTool({
      controls: this
    });

    this.brushControl = new BrushControl({
      isActive: this.determineIsBrushActive(),
      tool: this.brushTool,
      controls: this
    });
    this.colorControl = new ColorControl({
      isActive: this.determineIsBrushActive(),
      tool: this.brushTool,
      controls: this
    });
    this.eraseControl = new EraseControl({
      isActive: this.determineIsEraserActive(),
      tool: this.eraserTool,
      controls: this
    });

    this.list = el('.brush-controls', [
      this.brushControl,
      this.colorControl,
      this.eraseControl
    ]);

    this.button = this.renderButton();
    this.el = el('.brush-controls-main', {
      onmouseenter: this.handleMouseEnter.bind(this),
      onmouseleave: this.handleMouseLeave.bind(this)
    }, [
      this.button
    ]);

    if (this.isActive === true) {
      this.el.classList.add('active');
    }

  }

  initializeBrushTool() {

    if (this.determineIsBrushActive()) {
      return this.canvas.activeTool;
    }

    return new BrushTool({
      width: 10,
      opacity: 1,
      color: { r: 0, g: 0, b: 0 },
      controls: this
    });

  }

  determineIsBrushActive() {

    return (this.canvas.activeTool instanceof BrushTool);

  }

  determineIsEraserActive() {

    return (this.canvas.activeTool instanceof EraserTool);

  }

  determineActive() {

    return this.determineIsBrushActive() || this.determineIsEraserActive();

  }

  handleMouseEnter() {

    if (this.mouseLeaveTimer !== null) {
      clearTimeout(this.mouseLeaveTimer);
      this.mouseLeaveTimer = null;
      return;
    }

    mount(this.el, this.list);

    const element = this.list;
    const anchorElement = this.button;
    const viewportElement = void 0;
    const verticalOffset = 0;
    const horizontalOffset = 0;
    const startSide = 'top';
    const placeOnCenter = true;
    const positionableSides = ['top', 'bottom'];

    displayElementAroundAnchor(element, anchorElement, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides);

    this.brushControl.positionSizeInput();

  }

  handleMouseLeave() {

    this.mouseLeaveTimer = setTimeout(() => {
      unmount(this.el, this.list);
      this.mouseLeaveTimer = null;
    }, 500);

  }

  renderButton() {

    const options = {};

    if (typeof this.brush === 'string') {
      options.class = 'active';
    }

    const button = el('button.brush-control', options, brushControlsIcon.cloneNode(true));

    return button;

  }

  updateButton() {

    this.button.classList.remove('active');

    if (typeof this.brush === 'string') {
      this.button.classList.add('active');
    }

  }

  isActive(control) {

    if (this.brush === null) {
      return false;
    }

    return this.brush.brushType === control.type;

  }

  select(control) {

    if (this.brush === null) {
      this.brush = {
        brushType: control.type,
        color: '#000000',
        size: control.size
      };
      this.el.classList.add('active');
    } else if (this.brush.brushType === control.type) {
      this.brush = null;
      this.el.classList.remove('active');
    } else {
      this.brush.brushType = control.type;
    }

    this.onChange(this.brush);

    return this.brush !== null;

  }

  activateBrush() {

    this.isActive = true;
    this.el.classList.add('active');

    if (this.brushControl.isActive === false) {
      this.brushControl.activate();
    }

  }

  deactivateBrush() {

    this.isActive = false;
    this.el.classList.remove('active');

    if (this.brushControl.isActive === true) {
      this.brushControl.deactivate();
    }

  }

  toggleBrush() {

    if (this.determineIsBrushActive()) {
      this.canvas.setActiveTool(null);
      this.deactivateBrush();
    } else {
      this.canvas.setActiveTool(this.brushTool);
      this.activateBrush();
    }

  }

  deactivateEraserTool() {

    this.isActive = false;
    this.canvas.setActiveTool(null);
    this.el.classList.remove('active');

  }

  activateEraserTool() {

    this.isActive = true;
    this.canvas.setActiveTool(this.eraserTool);
    this.el.classList.add('active');

  }

}

class BrushTool {

  constructor(configuration) {

    this.width = configuration.width;
    this.color = configuration.color;
    this.opacity = configuration.opacity;
    this.controls = configuration.controls;
    this.brushActive = false;
    this.activeObject = null;

  }

  setActiveObject(activeObject) {

    this.activeObject = activeObject;

  }

  setWidth(width) {

    this.width = width;

  }

  setOpacity(opacity) {

    this.opacity = opacity;

  }

  setColor(color) {

    this.color = color;

  }

  deactivate() {

    this.activeObject.set({
      selectable: true,
      evented: true
    });

    this.activeObject = null;

    this.controls.deactivateBrush();

  }

  handleCanvasMouseDown(event, fabricCanvas, canvas) {

    this.brushActive = false;
    fabricCanvas.isDrawingMode = false;
    fabricCanvas.freeDrawingBrush = null;

    const pointer = fabricCanvas.getPointer(event.e);
    const imgBounds = this.activeObject.getBoundingRect();

    if (pointer.x >= imgBounds.left && pointer.x <= imgBounds.left + imgBounds.width &&
      pointer.y >= imgBounds.top && pointer.y <= imgBounds.top + imgBounds.height) {

      this.brushActive = true;
      fabricCanvas.isDrawingMode = true;
      fabricCanvas.freeDrawingBrush = new PencilBrush(fabricCanvas);
      fabricCanvas.freeDrawingBrush.color = getColorString(this.color, this.opacity);
      fabricCanvas.freeDrawingBrush.width = this.width;
      this.activeObject.set({
        selectable: false,
        evented: false
      });

      fabricCanvas.freeDrawingBrush.onMouseDown(pointer, event);
    }
    fabricCanvas.renderAll();

  }

  handleCanvasMouseUp(event, fabricCanvas, canvas) {

    this.brushActive = false;

    if (fabricCanvas.freeDrawingBrush) {
      fabricCanvas.freeDrawingBrush.onMouseUp(event);
    }
    fabricCanvas.isDrawingMode = false;
    fabricCanvas.freeDrawingBrush = null;

    this.activeObject.set({
      selectable: true,
      evented: true
    });

    fabricCanvas.renderAll();

  }

  handleCanvasMouseMove(event, fabricCanvas, canvas) {

    if (this.brushActive === false) {
      return;
    }

    const pointer = fabricCanvas.getPointer(event.e);
    const imgBounds = this.activeObject.getBoundingRect();

    if (pointer.x >= imgBounds.left && pointer.x <= imgBounds.left + imgBounds.width &&
      pointer.y >= imgBounds.top && pointer.y <= imgBounds.top + imgBounds.height) {
      fabricCanvas.freeDrawingBrush.color = getColorString(this.color, this.opacity);
      fabricCanvas.freeDrawingBrush.width = this.width;
      fabricCanvas.freeDrawingBrush.onMouseMove(pointer, event);

    } else {
      fabricCanvas.isDrawingMode = false;
      this.brushActive = false;
    }

  }

  handleCanvasPathCreated(path, fabricCanvas, canvas) {

    const groupBounds = this.activeObject.getBoundingRect();
    const pathBounds = path.getBoundingRect();

    // Check if path is inside activeGroup
    const isInside = (
      pathBounds.left >= groupBounds.left &&
      pathBounds.top >= groupBounds.top &&
      pathBounds.left + pathBounds.width <= groupBounds.left + groupBounds.width &&
      pathBounds.top + pathBounds.height <= groupBounds.top + groupBounds.height
    );

    fabricCanvas.remove(path);
    if (!isInside) {
      return;
    }

    path.set({
      selectable: false,
      evented: false
    });
    this.activeObject.add(path);
    path.setCoords();
    fabricCanvas.requestRenderAll();

  }

}

class EraserTool {

  constructor(configuration) {
    this.activeObject = null;
    this.controls = configuration.controls;
  }

  setActiveObject(activeObject) {

    this.activeObject = activeObject;

  }

  deactivate() {

    this.controls.isActive = false;
    this.controls.eraseControl.isActive = false;

    this.controls.el.classList.remove('active');
    this.controls.eraseControl.el.classList.remove('active');

  }

  handleCanvasMouseDown(event, fabricCanvas, canvas) {

    const pointer = fabricCanvas.getPointer(event.e);

    fabricCanvas.getObjects().forEach((obj) => {
      if (obj.type === 'group') {
        obj.getObjects().forEach((child) => {
          if (child.type === 'path' && child.containsPoint(pointer)) {
            obj.remove(child); // Remove from group
          }
        });
        obj.dirty = true; // Mark group as modified
      }
    });

    fabricCanvas.renderAll();

    canvas.updateObjectDetails();

  }

}

export default BrushControls;